import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Icon, IconSource } from '../icon'
import { Pressable } from '../pressable'
import { GLYPH_COLORS, MoreVerticalGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'

export type DoubleButtonProps = {
    /** Primary segment label. */
    label: string
    /** Optional leading icon node for the primary segment. */
    icon?: IconSource
    /** Primary action. */
    onPress?: () => void
    /** Secondary "more" (overflow) action on the trailing segment. */
    onMorePress?: () => void
    isDisabled?: boolean
    className?: string
    testID?: string
    /** Forwarded to the primary segment (e.g. for tooltip / DOM targeting). */
    nativeID?: string
}

const labelText = 'font-normal text-[0.875rem] leading-[1.25rem] text-aos-text-on-primary'
const segmentFill = 'bg-action-default hover:bg-action-hover active:bg-action-pressed'

/**
 * Split "double" button matching the Figma redesign (AOS-599): a primary
 * labelled action plus a trailing overflow (more) trigger, divided by a 1px
 * hairline. Presentational and controlled — `onPress` / `onMorePress` drive the
 * two segments.
 */
export const DoubleButton: React.FC<DoubleButtonProps> = ({
    label,
    icon,
    onPress,
    onMorePress,
    isDisabled,
    className,
    testID,
    nativeID,
}) => (
    <Box
        className={twMerge(
            'flex-row items-stretch gap-px self-start overflow-hidden rounded',
            isDisabled ? 'opacity-40' : '',
            className,
        )}
        testID={testID}
    >
        <Pressable
            className={twMerge('flex-row items-center gap-2 px-4 py-3', segmentFill)}
            onPress={isDisabled ? undefined : onPress}
            accessibilityRole='button'
            accessibilityLabel={label}
            nativeID={nativeID}
        >
            <Icon as={icon} size={24} className='text-aos-text-on-primary' />
            <Text className={labelText}>{label}</Text>
        </Pressable>
        <Pressable
            className={twMerge('items-center justify-center px-2 py-3', segmentFill)}
            onPress={isDisabled ? undefined : onMorePress}
            accessibilityRole='button'
            accessibilityLabel='More options'
        >
            <MoreVerticalGlyph size={24} color={GLYPH_COLORS.onPrimary} />
        </Pressable>
    </Box>
)

DoubleButton.displayName = 'DoubleButton'
